<template>
  <div class="committer-summary-table">
     <a-table :columns="columns" :data-source="tableData" :loading="props.loading" :pagination="false" :row-key="rowKey"
       size="small" :row-class-name="rowClassName" :on-row="onRowHandler" :scroll="{ x: 2200, y: 560 }">
      <template #bodyCell="{ column, record }">
        <span v-if="column.dataIndex === 'committer'">
          <span class="committer-id">{{ record.committer }}</span>
          <span v-if="record.email" class="committer-email">&lt;{{ record.email }}&gt;</span>
        </span>
        <span v-else-if="column.dataIndex === 'repoPath'" class="repo-path-cell">
          {{ record.repoPath }}
        </span>
        <span v-else-if="column.dataIndex === 'employerType'">
          <a-tag :color="employerTypeMeta(record.employerType).color">
            {{ employerTypeMeta(record.employerType).text }}
          </a-tag>
        </span>
        <span v-else-if="['changedAdditions', 'changedDeletions', 'prReviewCount', 'commentReviewCount'].includes(column.dataIndex)">
          {{ formatInteger(record[column.dataIndex]) }}
        </span>
        <span v-else-if="column.dataIndex === 'prReviewTime'">
          {{ formatDecimal(record.prReviewTime) }}
        </span>
      </template>
    </a-table>

    <div class="pagination-footer">
      <div class="pagination-summary">
          第 {{ props.pageVo.startIndex + 1 }}—{{ props.pageVo.endIndex }} 个 committers，共 {{ props.pageVo.totalRows }} 个 committers
      </div>
      <a-pagination :current="props.pageVo.curPage" :page-size="props.pageVo.pageSize" :page-size-options="['10', '20', '50', '100']"
        :total="props.pageVo.totalRows" show-size-changer show-quick-jumper @change="handlePageChange"
        @showSizeChange="handlePageSizeChange" />
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, ref } from 'vue';
import dayjs from 'dayjs';

const props = defineProps({
  records: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pageVo: {
    type: Object,
    default: () => ({
      totalRows: 0,
      curPage: 1,
      pageSize: 10,
      startIndex: 0,
      endIndex: 0,
    }),
  },
});

const emit = defineEmits(['page-change', 'page-size-change']);

const activeGroup = ref(null);

const employerTypeMeta = (employerType) => {
  if (employerType === 'HUAWEI') {
    return { text: '华为', color: 'red' };
  }
  if (employerType === 'NON_HUAWEI') {
    return { text: '非华为', color: 'blue' };
  }
  return { text: employerType || '-', color: 'default' };
};

// 后端已是“总-分”结构：每个 committer 下有 details。
// 这里仅做“拍平”给表格，并在拍平时一次性带上合并单元格所需元信息，
// 避免后面再次遍历 tableData 计算 rowSpan/group。
const tableData = computed(() => {
  return props.records.flatMap((item, groupIndex) => {
    const details = Array.isArray(item?.details) && item.details.length > 0
      ? item.details
      : [{}];

    return details.map((detail, detailIndex) => ({
      committer: item?.committer || '',
      email: item?.email || '',
      employerType: item?.employerType || '',
      repoPath: detail?.repoPath || '',
      startTime: detail?.startTime || '',
      endTime: detail?.endTime || '',
      changedAdditions: detail?.changedAdditions,
      changedDeletions: detail?.changedDeletions,
      prReviewTime: Number(detail?.prReviewTime || 0),
      prReviewCount: detail?.prReviewCount,
      commentReviewCount: detail?.commentReviewCount,
      __groupId: groupIndex,
      __rowSpan: detailIndex === 0 ? details.length : 0,
      __isGroupEnd: detailIndex === details.length - 1,
    }));
  });
});

const formatInteger = (value) => {
  return Number(value || 0).toLocaleString('en-US');
};

const formatDecimal = (value) => {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const columns = [
  {
    title: 'Committer',
    dataIndex: 'committer',
    key: 'committer',
    width: 320,
    // 仅组首行显示 committer；其余行 rowSpan=0 被合并隐藏。
    customCell: (record) => ({
      rowSpan: record.__rowSpan ?? 1,
      class: record.__rowSpan > 0 ? 'committer-group-cell' : '',
    }),
  },
  {
    title: '雇主类型',
    dataIndex: 'employerType',
    key: 'employerType',
    width: 130,
    // 与 committer 同步合并，保证左侧两列视觉上属于同一组。
    customCell: (record) => ({
      rowSpan: record.__rowSpan ?? 1,
      class: record.__rowSpan > 0 ? 'committer-group-cell' : '',
    }),
  },
  {
    title: '代码仓',
    dataIndex: 'repoPath',
    key: 'repoPath',
    className: 'repo-col',
    width: 520,
  },
  {
    title: '任职开始时间',
    dataIndex: 'startTime',
    key: 'startTime',
    width: 130,
    customRender: ({ text }) => (text ? dayjs(text).format('YYYY-MM-DD') : '至今'),
  },
  {
    title: '任职结束时间',
    dataIndex: 'endTime',
    key: 'endTime',
    width: 130,
    customRender: ({ text }) => (text ? dayjs(text).format('YYYY-MM-DD') : '至今'),
  },
  {
    title: '检视 PR 新增代码量（行）',
    dataIndex: 'changedAdditions',
    key: 'changedAdditions',
    width: 190,
  },
  {
    title: '检视 PR 删除代码量（行）',
    dataIndex: 'changedDeletions',
    key: 'changedDeletions',
    width: 190,
  },
  {
    title: '检视 PR 个数',
    dataIndex: 'prReviewCount',
    key: 'prReviewCount',
    width: 140,
  },
  {
    title: '检视时长（小时）',
    dataIndex: 'prReviewTime',
    key: 'prReviewTime',
    width: 150,
  },
  {
    title: '检视评论个数',
    dataIndex: 'commentReviewCount',
    key: 'commentReviewCount',
    width: 150,
  },
];

const handlePageChange = (page) => {
  emit('page-change', page);
};

const handlePageSizeChange = (current, size) => {
  emit('page-size-change', current, size);
};

const rowClassName = (record) => {
  const gid = record.__groupId;
  const isEnd = record.__isGroupEnd;
  // 组维度 hover 高亮 + 组末尾分隔线。
  return `committer-group-${gid} ${activeGroup.value === gid ? 'committer-active' : ''} ${isEnd ? 'committer-group-end' : ''}`;
};

const rowKey = (record) => {
  return `${record.committer || ''}_${record.email || ''}_${record.repoPath || ''}_${record.startTime || ''}_${record.endTime || ''}`;
};

const onRowHandler = (record) => ({
  onMouseenter: () => {
    // 按组高亮，不是按单行高亮。
    activeGroup.value = record.__groupId;
  },
  onMouseleave: () => {
    activeGroup.value = null;
  },
});
</script>

<style scoped>
.committer-summary-table {
  width: 100%;
}

.pagination-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
}

.pagination-summary {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.repo-path-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  white-space: nowrap;
}

.committer-summary-table :deep(.ant-table-cell) {
  white-space: nowrap;
}

.committer-id {
  font-weight: 600;
  color: #1f2937;
}

.committer-email {
  margin-left: 6px;
  color: #6b7280;
  font-size: 12px;
}

:deep(.ant-table-tbody > tr.committer-group-end > td) {
  border-bottom: 1px solid #CCCCCC !important;
}

:deep(.ant-table-tbody > tr > td.committer-group-cell) {
  border-bottom: 1px solid #CCCCCC !important;
}
</style>